import { Injectable, Inject, signal, computed } from '@angular/core';
import { Firestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User,
  Auth
} from 'firebase/auth';
import { FIRESTORE, firebaseApp } from '../../main';
import { UserProfile } from '../models/settings.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth: Auth;
  
  private currentUser = signal<User | null>(null);
  private userProfile = signal<UserProfile | null>(null);
  private loading = signal(true);

  readonly user = this.currentUser.asReadonly();
  readonly profile = this.userProfile.asReadonly();
  readonly isLoading = this.loading.asReadonly();
  readonly isLoggedIn = computed(() => this.currentUser() !== null);
  readonly isAdmin = computed(() => this.userProfile()?.role === 'admin');

  constructor(@Inject(FIRESTORE) private firestore: Firestore) {
    this.auth = getAuth(firebaseApp);
    
    onAuthStateChanged(this.auth, async (user) => {
      this.currentUser.set(user);
      if (user) {
        // Wacht kort om auth state te laten synchroniseren met Firestore
        await new Promise(resolve => setTimeout(resolve, 100));
        await this.loadUserProfile(user.uid);
      } else {
        this.userProfile.set(null);
      }
      this.loading.set(false);
    });
  }

  private async loadUserProfile(uid: string): Promise<void> {
    try {
      // Forceer een verse token om te garanderen dat auth volledig is
      await this.auth.currentUser?.getIdToken(true);
      
      const userDoc = await getDoc(doc(this.firestore, 'users', uid));
      if (userDoc.exists()) {
        this.userProfile.set({ uid, ...userDoc.data() } as UserProfile);
      } else {
        // Geen profiel gevonden - dit is OK, user is wel ingelogd
        this.userProfile.set(null);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      this.userProfile.set(null);
    }
  }

  async login(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
    } catch (error) {
      throw error;
    }
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
  }

  async createAdminProfile(uid: string, email: string): Promise<void> {
    const profile: Omit<UserProfile, 'uid'> = {
      email,
      role: 'admin',
      createdAt: new (await import('firebase/firestore')).Timestamp(Date.now() / 1000, 0)
    };
    await setDoc(doc(this.firestore, 'users', uid), profile);
  }
}
